/**
 * Helpers for the .NET TimeSpan string used by WorkflowNodeDefinition.Delay.
 * Format: `[d.]hh:mm:ss` (e.g. "02:30:00" or "1.02:30:00").
 */

export interface DurationParts {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
}

const ZERO: DurationParts = { days: 0, hours: 0, minutes: 0, seconds: 0 };

const PATTERN = /^(?:(\d+)\.)?(\d{1,2}):(\d{2}):(\d{2})(?:\.\d+)?$/;

/** Parse a TimeSpan-style string into parts. Empty/invalid input returns zero. */
export function parseDuration(value: string | null | undefined): DurationParts {
  if (!value) return { ...ZERO };
  const match = PATTERN.exec(value.trim());
  if (!match) return { ...ZERO };
  return {
    days: Number(match[1] ?? 0),
    hours: Number(match[2]),
    minutes: Number(match[3]),
    seconds: Number(match[4])
  };
}

/** Format parts as `[d.]hh:mm:ss`. Negative components are clamped to zero. */
export function formatDuration(parts: Partial<DurationParts>): string {
  const d = Math.max(0, Math.floor(parts.days ?? 0));
  const h = Math.max(0, Math.floor(parts.hours ?? 0));
  const m = Math.max(0, Math.floor(parts.minutes ?? 0));
  const s = Math.max(0, Math.floor(parts.seconds ?? 0));
  const hh = String(h).padStart(2, '0');
  const mm = String(m).padStart(2, '0');
  const ss = String(s).padStart(2, '0');
  return d > 0 ? `${d}.${hh}:${mm}:${ss}` : `${hh}:${mm}:${ss}`;
}

/** True when all parts are zero. */
export function isZeroDuration(parts: DurationParts): boolean {
  return parts.days === 0 && parts.hours === 0 && parts.minutes === 0 && parts.seconds === 0;
}
